<template>
    <div>
        <label
            :for="id"
            class="flex items-center text-sm font-medium leading-6 ct-text-2 space-x-1 mb-2"
        >
            <span>{{ title }} </span>
        </label>
        <div class="flex items-center space-x-2">
            <div class="relative w-full">
                <input
                    :id="id"
                    ref="input"
                    v-model="model"
                    class="ct-input !text-gray-500 dark:!text-neutral-400 !pl-14 h-11 w-full"
                />

                <label
                    :style="{ 'background-color': modelWithPrefix }"
                    class="absolute -translate-y-1/2 top-1/2 left-2 cursor-pointer flex-shrink-0 h-6 w-6 border ct-border-1 rounded-md"
                    aria-hidden="true"
                    ><input v-model="model" type="color" class="invisible h-full"
                /></label>

                <p class="absolute inset-y-0 left-10 flex items-center ct-text-4">
                    {{ prefix }}
                </p>

                <ul class="absolute -translate-y-1/2 top-1/2 right-2 flex gap-1">
                    <li
                        v-for="(color, index) in pageColors"
                        :key="index"
                        class="cursor-pointer h-6 w-6 rounded-md ring-inset flex items-center justify-center"
                        :class="
                            selectedThemeColor === index
                                ? 'ring-2 ct-ring-focus'
                                : 'ring-1 ct-ring-1 hover:ct-ring-brand'
                        "
                        :style="{ 'background-color': color.hex }"
                        @click="selectedThemeColor = index"
                        v-tooltip="color.label"
                    >
                        <CheckIcon
                            class="w- h-4"
                            :class="[isColorDark(color.hex) ? 'text-white' : 'text-black']"
                            v-if="selectedThemeColor === index"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { isColorDark, randomId } from "@/shared/utils/helpers";
import { CheckIcon } from "@easy/ui/icons";

const props = defineProps({
    modelValue: {
        type: String,
        default: "",
    },
    title: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["update:modelValue"]);

const store = useStore();
const prefix = ref("#");
const selectedThemeColor = ref(null);
const id = ref(randomId());
const input = ref(null);

const pageColors = computed(() => store.getters["page/theme/pageColors"]);

const model = computed({
    get() {
        if (props.modelValue && props.modelValue.startsWith(prefix.value)) {
            return props.modelValue.slice(prefix.value.length);
        }

        if (props.modelValue && props.modelValue.startsWith("var")) {
            return pageColors.value[selectedThemeColor.value]?.hex?.slice(prefix.value.length);
        }

        return props.modelValue;
    },
    set(value) {
        if (!value) {
            emit("update:modelValue", "");
            return;
        }

        if (value.startsWith(prefix.value)) {
            emit("update:modelValue", value);
        } else {
            emit("update:modelValue", prefix.value + value);
        }
    },
});

const modelWithPrefix = computed(() => prefix.value + model.value);

onMounted(() => {
    if (props.modelValue.startsWith("var")) {
        selectedThemeColor.value = pageColors.value.findIndex(
            (color) => color.value === props.modelValue
        );
    }
});

watch(
    () => props.modelValue,
    (value) => {
        if (value.startsWith(prefix.value)) {
            selectedThemeColor.value = null;
        }
    },
    { immediate: true }
);

watch(selectedThemeColor, (index) => {
    if (index === null) {
        return;
    }
    emit("update:modelValue", pageColors.value[index].value);
});
</script>
